/* Copyright (c) 2020-present CloudIO Inc */

import BoxManager from '../Box/BoxManager';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './BoxList';
import { baseComponentManager } from '../../registry/Components';

const BoxListManager: ComponentManagerFn<'BoxList'> = (platform: Platform) => {
  const boxManager = BoxManager(platform, 'Box');
  return {
    ...baseComponentManager<'BoxList'>(platform, 'BoxList'),
    defaultValue,
    props: {
      ...boxManager.props,
      noDataFoundMessage: {
        label: 'No Data Found Message',
        type: 'string',
      },
      useSelectedRows: {
        label: 'Use Selected Rows',
        type: 'boolean',
      },
      reorderAttribute: {
        label: 'Reorder Attribute',
        type: 'ViewAttributeChooser',
        category: 'Reorder',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Integer', 'Decimal', 'Double'],
        preventMultiEdit: true,
      },
      reorderAxis: {
        label: 'Reorder Axis',
        type: 'select',
        category: 'Reorder',
        options: { x: 'X', y: 'Y' },
      },
      canReorderCondition: {
        label: 'Can Reorder Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
        category: 'Reorder',
        helpText:
          'e.g. $._.rowIndex !== 0 to prevent the first item from being reordered.',
      },
      onReorderControllerFunction: {
        label: 'On Reorder Controller Function',
        type: 'controllerFunction',
        category: 'Reorder',
        alwaysShow: true,
        helpText: `e.g. 
\`\`\`javascript
function onReorder({ oldOrder, newOrder, store }) {
  // oldOrder is the old order of the items
  // newOrder is the new order of the items
  const id = newOrder.find((recordId) => store.getRecord(recordId)["pe_category"] === "BASE_PRICE");
  if (id) {
    newOrder = newOrder.filter((recordId) => recordId !== id);
    newOrder.unshift(id);
  }
  // return oldOrder to prevent this reorder
  return newOrder;
}
\`\`\`
`,
      },
    },
    isPropertyVisible: (property, itemData, context) => {
      switch (property) {
        case 'alignSelf':
        case 'border':
        case 'borderRadius':
        case 'boxShadow':
        case 'color':
        case 'component':
        case 'display':
        case 'fontSize':
        case 'fontStyle':
        case 'fontWeight':
        case 'overflow':
        case 'showOnlyOnHoverItemIds':
          return false;
        case 'noDataFoundMessage':
        case 'useSelectedRows':
          return true;
        case 'reorderAttribute':
        case 'reorderAxis':
        case 'canReorderCondition':
        case 'onReorderControllerFunction':
          return true;
        default:
          return boxManager.isPropertyVisible(property, itemData, context);
      }
    },
    type: 'BoxList',
    name: 'Box List',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'Box',
          'CollectionList',
          'DragSourceBox',
          'DropTargetBox',
          'MetricBox',
          'CalendarSourceEvent',
        ] as ItemType[]
      ).includes(childType),
    icon: { iconName: 'boxes' },
    category: 'Data Containers',
    replaceableWith: ['Box'],
    canHaveChildren: () => true,
    prependOverlay: true,
    description:
      'Box List component is used to render records as the list inside box. User can use this component to design interactive boxes or list.',
  };
};

export default BoxListManager;
